import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { QuestionMarkCircle } from '@vy/ui-design-system';
import { AGGREGATION_OPERATORS } from '@vy/types';
import { FC } from 'react';
import { VyTooltip } from '../../../../../components/new-design/VyTooltip';
import { getTableColumns } from '../constants/table-columns';
import type { UsageDataTableProps } from '../types';
import type { UsageLineData } from '../types/usage-items';
import { formatEntryToTableRow } from '../utils/dynamic-line-item-formatters';
import { usageDataTableStyles } from './styles';
import { dynamicInvoicePageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { useTreatLastAsDaily } from '../../../../../hooks/use-treat-last-as-daily';
import { notDefined } from '@vy/null';

const columnKeyToSelector: Partial<Record<string, string>> = {
  from: dynamicInvoicePageSelectors.content.fromField,
  to: dynamicInvoicePageSelectors.content.toField,
  unitPrice: dynamicInvoicePageSelectors.content.perUnitField,
  bulkSize: dynamicInvoicePageSelectors.content.bulkSizeField,
  flatRate: dynamicInvoicePageSelectors.content.fixedRateField,
  ytdQty: dynamicInvoicePageSelectors.content.ytdQtyField,
  periodQty: dynamicInvoicePageSelectors.content.qtyField,
  billableQty: dynamicInvoicePageSelectors.content.billableQtyField,
  amount: dynamicInvoicePageSelectors.content.amountField,
};

export const UsageDataTable: FC<UsageDataTableProps> = ({
  period,
  pricingModel,
  currency,
  isRevShare = false,
  hasBillingPeriods = true,
  aggregationOperator,
}) => {
  const treatLastAsDaily = useTreatLastAsDaily();

  if (notDefined(period)) {
    return null;
  }

  const showSeatDays = aggregationOperator === AGGREGATION_OPERATORS.Last && treatLastAsDaily;
  const tableColumns = getTableColumns(showSeatDays);

  const tableData: UsageLineData[] = period.entries.map((entry) =>
    formatEntryToTableRow(entry, pricingModel, currency, isRevShare, showSeatDays),
  );

  return (
    <Table sx={usageDataTableStyles.table}>
      <TableHead>
        <TableRow>
          {tableColumns.map(({ key, label, hasDivider, tooltip }, index) => (
            <TableCell
              key={key}
              sx={{
                ...usageDataTableStyles.tableHeader,
                ...(hasDivider ? usageDataTableStyles.verticalDivider : {}),
                ...(!hasBillingPeriods && index === 0 ? usageDataTableStyles.tableHeaderFirst : {}),
                ...(!hasBillingPeriods && index === tableColumns.length - 1
                  ? usageDataTableStyles.tableHeaderLast
                  : {}),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {label}
                {tooltip && (
                  <VyTooltip
                    placement="top"
                    tooltipContent={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>}
                  >
                    <Box component="span" sx={{ display: 'flex', cursor: 'pointer' }}>
                      <QuestionMarkCircle />
                    </Box>
                  </VyTooltip>
                )}
              </Box>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {tableData.map((row: UsageLineData, index: number) => (
          <TableRow key={index} sx={usageDataTableStyles.tableRow}>
            {tableColumns.map(({ key, hasDivider }) => {
              const selector = columnKeyToSelector[key];
              return (
                <TableCell
                  key={key}
                  sx={{
                    ...(key === 'amount'
                      ? usageDataTableStyles.tableCellAmount
                      : usageDataTableStyles.tableCell),
                    ...(hasDivider ? usageDataTableStyles.verticalDivider : {}),
                  }}
                  {...(selector ? getSelectorAttribute(selector) : {})}
                >
                  {row[key as keyof typeof row]}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
